import { useState } from "react";
import { EVENT5K } from "@/constants/testIds";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { HeartHandshake } from "lucide-react";

const ROLES = [
  { key: "setup", label: "Setup crew" },
  { key: "checkin", label: "Check-in & bibs" },
  { key: "water", label: "Water station" },
  { key: "fun_run", label: "1-Mile Fun Run support" },
  { key: "vendor_support", label: "Vendor support" },
  { key: "guest_support", label: "Guest & family support" },
  { key: "breakdown", label: "Breakdown crew" },
];

const empty = { first_name: "", last_name: "", email: "", phone: "", tshirt_size: "", availability: [], notes: "" };

export function VolunteerDialog({ open, onOpenChange }) {
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleRole = (k) => setForm((f) => ({
    ...f,
    availability: f.availability.includes(k) ? f.availability.filter((x) => x !== k) : [...f.availability, k],
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email) {
      toast.error("Please fill required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/volunteers", form);
      setDone(true);
      toast.success("Thanks for volunteering!");
    } catch (err) {
      toast.error("Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) { setDone(false); setForm(empty); }}}>
      <DialogContent data-testid={EVENT5K.volunteerDialog} className="max-w-2xl bg-[#0A0A0A] border-[#27272A] text-white max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#00A651] font-bold">Volunteer Signup</div>
          <DialogTitle className="font-display text-3xl uppercase">Be the reason race day rocks.</DialogTitle>
          <DialogDescription className="text-[#A3A3A3]">
            Help with check-in, water stations, setup, breakdown, youth fun run support, vendor support, and more.
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="py-4">
            <div className="bg-[#141414] border border-[#00A651]/50 p-6">
              <div className="text-[11px] uppercase tracking-[0.28em] text-[#00A651] font-bold">You&apos;re on the team</div>
              <p className="mt-2 text-[#E5E5E5]">We&apos;ll follow up with your assignment, shift time, and where to check in.</p>
            </div>
            <button className="mt-6 border-2 border-white/40 hover:border-[#00A651] hover:text-[#00A651] font-bold uppercase tracking-widest text-sm px-6 py-3" onClick={() => onOpenChange(false)}>Close</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="First name" required>
                <Input data-testid={EVENT5K.volunteerFirst} value={form.first_name} onChange={(e) => update("first_name", e.target.value)} required />
              </Field>
              <Field label="Last name" required>
                <Input data-testid={EVENT5K.volunteerLast} value={form.last_name} onChange={(e) => update("last_name", e.target.value)} required />
              </Field>
              <Field label="Email" required>
                <Input data-testid={EVENT5K.volunteerEmail} type="email" value={form.email} onChange={(e) => update("email", e.target.value)} required />
              </Field>
              <Field label="Phone">
                <Input value={form.phone} onChange={(e) => update("phone", e.target.value)} />
              </Field>
              <Field label="T-shirt size">
                <Input value={form.tshirt_size} onChange={(e) => update("tshirt_size", e.target.value)} placeholder="M, L, XL…" />
              </Field>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-[0.24em] text-[#A3A3A3]">Where can you help?</Label>
              <div className="mt-2 grid sm:grid-cols-2 gap-2">
                {ROLES.map((r) => (
                  <label key={r.key} className={`flex items-center gap-3 p-3 border cursor-pointer transition-colors ${form.availability.includes(r.key) ? "border-[#00A651] bg-[#00A651]/5" : "border-[#27272A]"}`}>
                    <Checkbox checked={form.availability.includes(r.key)} onCheckedChange={() => toggleRole(r.key)} className="border-[#00A651]" />
                    <span className="text-sm">{r.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <Field label="Notes">
              <Textarea rows={2} value={form.notes} onChange={(e) => update("notes", e.target.value)} />
            </Field>

            <button data-testid={EVENT5K.volunteerSubmit} disabled={submitting} className="w-full bg-[#00A651] hover:bg-[#1FBF3E] text-black font-bold uppercase tracking-widest text-sm py-4 transition-all">
              {submitting ? "Submitting…" : "Sign me up →"}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function VolunteerSection({ onSignup }) {
  return (
    <section id="volunteer" data-testid={EVENT5K.sectionVolunteer} className="relative bg-[#0A0A0A] py-24 md:py-32 border-t border-[#27272A]">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#00A651] font-bold mb-4">Volunteer</div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black uppercase tracking-tight text-white">
              Race day is powered by <span className="text-[#EC008C]">volunteers</span>.
            </h2>
            <p className="mt-4 text-lg text-[#A3A3A3]">
              Help with check-in, water stations, setup, breakdown, youth fun run support, vendor support, and guest support. It&apos;s fun, it&apos;s fast, it changes lives.
            </p>
            <button
              data-testid={EVENT5K.volunteerBtn}
              onClick={onSignup}
              className="mt-8 bg-[#00A651] hover:bg-[#1FBF3E] text-black font-bold uppercase tracking-widest text-sm px-8 py-4 transition-all hover:-translate-y-0.5"
            >
              Sign Up to Volunteer →
            </button>
          </div>
          <div className="lg:col-span-5">
            <div className="relative bg-[#141414] border border-[#27272A] overflow-hidden">
              <img
                src="/images/volunteers-2025.png"
                alt="Volunteers at the 2025 Run to Empower Entrepreneurs 5K"
                loading="lazy"
                className="w-full aspect-[4/3] object-cover"
              />
              <div className="p-6">
                <HeartHandshake className="w-8 h-8 text-[#EC008C] mb-3" />
                <blockquote className="font-display text-xl md:text-2xl leading-tight uppercase tracking-tight text-white">
                  &ldquo;Every entrepreneur remembers who stood in the rain to hand them water at mile one.&rdquo;
                </blockquote>
                <div className="mt-3 text-[10px] uppercase tracking-[0.28em] text-[#00A651] font-bold">
                  2025 Volunteer Crew · Freedom In Me Business Showers
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.24em] text-[#A3A3A3]">
        {label} {required && <span className="text-[#EC008C]">*</span>}
      </Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
